'use client';

import { motion, useReducedMotion } from 'framer-motion';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const ROLL_IN_DURATION_S = 0.9;
const ROLL_IN_STAGGER_S = 0.045;
const ROLL_IN_EASE = [0.2, 0.8, 0.2, 1] as const;

const countDigitsAfter = (characters: string[], index: number) =>
  characters.slice(index + 1).filter((character) => /\d/.test(character)).length;

type RollingNumberProps = {
  value: string;
  /**
   * Entrance mode: digits start at 0 on mount and roll to the value, rightmost
   * digit first, and later changes roll with the same stagger. Leave it off
   * for values that should appear settled and only roll when they change.
   */
  rollIn?: boolean;
};

export function RollingNumber({ value, rollIn = false }: RollingNumberProps) {
  const shouldReduceMotion = useReducedMotion();
  const characters = Array.from(value);

  return (
    <span className="relative inline-block align-baseline tabular-nums">
      <span className="sr-only">{value}</span>
      <span className="invisible whitespace-pre" aria-hidden="true">
        {value}
      </span>
      <span className="absolute inset-0 inline-flex" aria-hidden="true">
        {characters.map((character, index) =>
          /\d/.test(character) ? (
            <span
              key={value.length - index}
              className="relative inline-block h-[1lh] w-[1ch] overflow-hidden"
            >
              <motion.span
                className="absolute left-0 flex flex-col"
                initial={rollIn && !shouldReduceMotion ? { top: '0%' } : false}
                animate={{ top: `${-Number(character) * 100}%` }}
                transition={
                  rollIn
                    ? {
                        duration: shouldReduceMotion ? 0 : ROLL_IN_DURATION_S,
                        ease: ROLL_IN_EASE,
                        delay: shouldReduceMotion
                          ? 0
                          : countDigitsAfter(characters, index) * ROLL_IN_STAGGER_S,
                      }
                    : { duration: shouldReduceMotion ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }
                }
              >
                {DIGITS.map((digit) => (
                  <span key={digit} className="block h-[1lh] shrink-0">
                    {digit}
                  </span>
                ))}
              </motion.span>
            </span>
          ) : (
            <span key={value.length - index}>{character}</span>
          ),
        )}
      </span>
    </span>
  );
}
